// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import type { SimplePageContent } from "@tmi/types/entities";
import { cache, CacheClosedError, simplePages } from "@/services/cache";
import { createInitLatch } from "@/stores/init-latch";

import { refreshSimplePagesFromServer } from "@/services/data/sync/simple-page-sync-coordinator";
import { NotFound, ServiceUnavailable } from "@tmi/types/api/responses";
import { retryOnceOnServiceUnavailable } from "@/utils/service-unavailable-retry";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allSimplePages.value.length > 0 );
const loading = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

const allSimplePagesBySlugMap = new Map< string, SimplePageContent >();
const defaultSimplePages: SimplePageContent[] = [];
const allSimplePages = ref< SimplePageContent[] >( defaultSimplePages );

export const useSimplePagesStore = defineStore( 'simple-pages', () => {
    // UX roadmap #19: the read in flight, so a concurrent caller (the on-demand lookup below) waits for the same one
    // instead of returning at once to an empty map and fetching the whole catalog from the server beside it.
    // It resolves to the 503 its catalog read got, if it got one (tmi-978), so the lookup waits that out before asking again.
    let initialization: Promise< ServiceUnavailable | undefined > | null = null;

    function reset(): void {
        allSimplePages.value.splice( 0 );
        allSimplePagesBySlugMap.clear();
        initialization = null;

        loading.value = false;
        initLatch.clear();
    }

    async function initializeAsync(): Promise< void > {
        await startInitialization();
    }

    function startInitialization(): Promise< ServiceUnavailable | undefined > {
        if ( initialization ) {
            return initialization;
        }

        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Simple-Pages ].initialize() exiting - already processing...' );
            return Promise.resolve( undefined );
        }

        const run: Promise< ServiceUnavailable | undefined > = runInitialization().finally( () => {
            if ( initialization === run ) {
                initialization = null;
            }
        } );
        initialization = run;

        return run;
    }

    async function runInitialization(): Promise< ServiceUnavailable | undefined > {
        try {
            logger.debug( `[ Simple-Pages ] Checking the local database for any simple page content...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            const uniquePages: Map< string, SimplePageContent > = new Map< string, SimplePageContent >();
            if ( !await simplePages.isEmpty() ) {
                // Clear the contents of the searchable collections
                allSimplePages.value.splice( 0 );

                for ( const page of await simplePages.all() ) {
                    uniquePages.set( page.id, page );
                }

                allSimplePages.value = [ ...( Array.from( uniquePages.values() ) ) ];
                // UX roadmap #19: the lookup reads this map. Left empty on this path, every page view fetched the
                // whole catalog from the server again although the cache held it.
                allSimplePages.value.forEach( ( page: SimplePageContent ) => allSimplePagesBySlugMap.set( page.slug, page ) );

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allSimplePages.value.length } simple-pages in ${ t1 - t0 } ms` );
            } else {
                // A 503 here is not a cache failure: nothing is latched, and the next lookup reads again.
                const failure: unknown = await fetchFromServer();
                return failure instanceof ServiceUnavailable ? failure : undefined;
            }
        } catch ( err: any ) {
            if ( err instanceof CacheClosedError ) { initLatch.halt( err ); return undefined; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( err.message ) );
            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }

        return undefined;
    }

    // WHY: Concurrent callers must await the SAME fetch promise. The previous
    // `if ( loading.value ) return;` early-return let the second caller skip
    // the work and observe an empty map → the original double-fetch race in
    // SimpleContentfulPage threw NotFound and rendered blank content tabs.
    // Resolves to the failure, if any, rather than rejecting: each caller decides what a failure means to it.
    let inFlightFetch: Promise< unknown > | null = null;

    function fetchFromServer(): Promise< unknown > {
        if ( inFlightFetch ) {
            return inFlightFetch;
        }

        const run: Promise< unknown > = ( async () => {
            try {
                loading.value = true;

                // tmi-978: a 503 before content's first cache fill rejects here instead of answering an empty catalog.
                const receivedSimplePages: SimplePageContent[] =
                    await refreshSimplePagesFromServer( { throwOnServiceUnavailable: true } );
                for ( const sp of receivedSimplePages ) {
                    allSimplePagesBySlugMap.set( sp.slug, sp );
                }

                allSimplePages.value = [ ...( Array.from( allSimplePagesBySlugMap.values() ) ) ];
                return undefined;
            } catch ( err: any ) {
                logger.error( JSON.stringify( err.message ) );
                // A warming catalog says nothing about the pages already held, so they stay.
                if ( !( err instanceof ServiceUnavailable ) ) {
                    reset();
                }
                return err;
            } finally {
                loading.value = false;
            }
        } )().finally( () => {
            if ( inFlightFetch === run ) {
                inFlightFetch = null;
            }
        } );
        inFlightFetch = run;

        return run;
    }

    async function fetchSimplePageBySlugIfMissing( slug: string,
                                                   options: { signal?: AbortSignal } = {} ): Promise< SimplePageContent | undefined > {
        // Cache hit — return immediately.
        let requestedSimplePage: SimplePageContent | undefined = allSimplePagesBySlugMap.get( slug );
        if ( requestedSimplePage ) {
            return requestedSimplePage;
        }

        // UX roadmap #19: pages load on demand, not in the boot every visitor waited for. The local cache first (a
        // member's sync keeps it current; an empty one fetches the catalog from the server itself), then the server
        // for a page the cache does not hold yet.
        const warming: ServiceUnavailable | undefined = await startInitialization();
        requestedSimplePage = allSimplePagesBySlugMap.get( slug );
        if ( requestedSimplePage ) {
            return requestedSimplePage;
        }

        // WHY: do NOT wipe the cache before re-fetching. The previous version
        // cleared `allSimplePagesBySlugMap` here, which (a) discarded valid
        // pages other tabs/components depend on and (b) created the race
        // window that broke TSP's Model Portfolio tab on first navigation.
        // `fetchFromServer` is now safe to call concurrently — multiple callers
        // share one in-flight promise.
        //
        // tmi-978: a 503 (content's cache has not filled yet, ~8 s after a restart) is retried once after its
        // Retry-After, and a second one rejects: the page shows a failed load, not NotFound. When the empty-cache read
        // above just got that 503, its wait comes first rather than a second request at once. Leaving the page aborts
        // the wait and sends no retry.
        let pendingWarming: ServiceUnavailable | undefined = warming;
        await retryOnceOnServiceUnavailable( async () => {
            if ( pendingWarming ) {
                const alreadyAnswered: ServiceUnavailable = pendingWarming;
                pendingWarming = undefined;
                throw alreadyAnswered;
            }

            const failure: unknown = await fetchFromServer();
            if ( failure instanceof ServiceUnavailable ) {
                throw failure;
            }
        }, { signal: options.signal } );

        requestedSimplePage = allSimplePagesBySlugMap.get( slug );
        if ( !requestedSimplePage ) {
            throw new NotFound();
        }

        return requestedSimplePage;
    }

    return {
        // ACTIONS
        fetchSimplePageBySlugIfMissing,
        initializeAsync,
        reset,

        // GETTERS
        allSimplePages
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSimplePagesStore, import.meta.hot ) );
}
